if (!customElements.get('minimum-first-order')) {
  class MinimumFirstOrder extends HTMLElement {
    constructor() {
      super();

      this.items = Array.from(this.querySelectorAll('[data-minimum-first-order-item]'));
      this.submitButton = this.querySelector('[data-add-recommended-order]');
      this.totalElement = this.querySelector('[data-order-total]');
      this.statusElement = this.querySelector('[data-status]');
      this.errorElement = this.querySelector('[data-error]');
      this.moneyLocale = this.dataset.moneyLocale || document.documentElement.lang || navigator.language || 'en-US';
      this.isAuthenticated = this.dataset.authenticated === 'true';
      this.totalLoadingMessage = this.dataset.totalLoadingMessage || 'Loading...';
      this.spark = null;
      this.isSubmitting = false;
      this.refreshTimers = new Map();
      this.priceRetryTimers = new Map();
      this.inFlightPrice = new Set();
      this.reconcileTimer = null;
      this.maxPriceAttempts = 6;
      this.pricingGeneration = 0;
      this.isAccountContextRefreshPending = false;
      this.accountContextRefreshTimer = null;
      this.accountContextRefreshFrame = null;
      this.hasAccountContextListeners = false;
      this.handleImpersonationContextChange = this.scheduleAccountContextRefresh.bind(this);
    }

    connectedCallback() {
      if (this.initialized) {
        this.bindAccountContextListeners();
        return;
      }
      this.initialized = true;

      if (!this.items.length) {
        this.updateOrderTotal();
        this.updateSubmitState();
        return;
      }

      this.bindQuantityHandlers();

      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.handleSubmit.bind(this));
      }

      if (!this.isAuthenticated) {
        this.applyGuestState();
        return;
      }

      this.bindAccountContextListeners();
      this.initializeSpark();
    }

    disconnectedCallback() {
      this.unbindAccountContextListeners();
      this.cancelAccountContextRefresh();
      this.cancelPendingPriceRefreshes();
      this.stopPriceReconciliation();
      this.pricingGeneration += 1;
      this.isAccountContextRefreshPending = false;
    }

    bindAccountContextListeners() {
      if (!this.isAuthenticated || this.hasAccountContextListeners) return;

      window.addEventListener('impersonation-sign-in', this.handleImpersonationContextChange);
      window.addEventListener('impersonation-sign-out', this.handleImpersonationContextChange);
      this.hasAccountContextListeners = true;
    }

    unbindAccountContextListeners() {
      if (!this.hasAccountContextListeners) return;

      window.removeEventListener('impersonation-sign-in', this.handleImpersonationContextChange);
      window.removeEventListener('impersonation-sign-out', this.handleImpersonationContextChange);
      this.hasAccountContextListeners = false;
    }

    cancelPendingPriceRefreshes() {
      this.refreshTimers.forEach((timeoutId) => window.clearTimeout(timeoutId));
      this.refreshTimers.clear();
      this.priceRetryTimers.forEach((timeoutId) => window.clearTimeout(timeoutId));
      this.priceRetryTimers.clear();
    }

    cancelAccountContextRefresh() {
      if (this.accountContextRefreshTimer !== null) {
        window.clearTimeout(this.accountContextRefreshTimer);
        this.accountContextRefreshTimer = null;
      }

      if (this.accountContextRefreshFrame !== null) {
        window.cancelAnimationFrame(this.accountContextRefreshFrame);
        this.accountContextRefreshFrame = null;
      }
    }

    scheduleAccountContextRefresh() {
      if (!this.isAuthenticated) return;

      // SparkLayer dispatches impersonation events before its active account state is updated.
      this.pricingGeneration += 1;
      this.isAccountContextRefreshPending = true;
      this.cancelPendingPriceRefreshes();
      this.cancelAccountContextRefresh();
      this.stopPriceReconciliation();

      this.accountContextRefreshTimer = window.setTimeout(() => {
        this.accountContextRefreshTimer = null;
        this.accountContextRefreshFrame = window.requestAnimationFrame(() => {
          this.accountContextRefreshFrame = null;
          this.refreshAccountContextPricing();
        });
      }, 50);

      this.updateSubmitState();
    }

    async refreshAccountContextPricing() {
      const generation = this.pricingGeneration;

      if (!this.spark || !this.isAuthenticated) {
        if (generation === this.pricingGeneration) {
          this.isAccountContextRefreshPending = false;
          this.updateSubmitState();
        }
        return;
      }

      this.items.forEach((item) => {
        item.dataset.valid = 'false';
        this.clearStoredPricing(item);
        item.dataset.pricingState = 'loading';
        this.renderPriceValue(item, 'Loading...', { loading: true });
        this.renderPriceMeta(item, '');
      });
      this.updateOrderTotal();
      this.updateSubmitState();

      await Promise.all(this.items.map((item) => this.refreshPrice(item, generation)));

      if (generation !== this.pricingGeneration) return;

      this.isAccountContextRefreshPending = false;
      this.updateOrderTotal();
      this.updateSubmitState();
      this.startPriceReconciliation();
    }

    bindQuantityHandlers() {
      this.items.forEach((item) => {
        const input = item.querySelector('.minimum-first-order__quantity-input');
        if (!input) return;

        input.addEventListener('input', () => {
          this.queuePriceRefresh(item);
        });

        input.addEventListener('change', () => {
          this.queuePriceRefresh(item, true);
        });
      });
    }

    async initializeSpark() {
      this.spark = await this.waitForSpark();

      if (!this.spark) {
        this.applyUnavailableState(this.dataset.errorMessage);
        return;
      }

      try {
        const isLoggedIn = typeof this.spark.isLoggedIn === 'function' ? await this.spark.isLoggedIn() : true;

        if (!isLoggedIn) {
          this.applyGuestState();
          return;
        }

        await Promise.all(this.items.map((item) => this.refreshPrice(item)));
        this.startPriceReconciliation();
      } catch (error) {
        console.error('[MinimumFirstOrder] SparkLayer initialization failed', error);
        this.applyUnavailableState(this.dataset.errorMessage);
      }
    }

    waitForSpark() {
      const hasSparkApi = () => {
        return Boolean(
          window.spark &&
            typeof window.spark.calculatePricingForVariant === 'function' &&
            typeof window.spark.updateCart === 'function'
        );
      };

      if (hasSparkApi()) {
        return Promise.resolve(window.spark);
      }

      return new Promise((resolve) => {
        let attempts = 0;
        const intervalId = window.setInterval(() => {
          attempts += 1;

          if (hasSparkApi()) {
            window.clearInterval(intervalId);
            resolve(window.spark);
            return;
          }

          if (attempts >= 60) {
            window.clearInterval(intervalId);
            resolve(null);
          }
        }, 250);
      });
    }

    applyGuestState() {
      this.items.forEach((item) => {
        item.dataset.valid = 'false';
        this.clearStoredPricing(item);
        item.dataset.pricingState = 'unavailable';
        this.renderPriceValue(item, this.dataset.guestMessage || 'SparkLayer login required.', {
          unavailable: true,
        });
        this.renderPriceMeta(item, '');
      });

      this.updateOrderTotal();
      this.updateSubmitState(true);
    }

    applyUnavailableState(message) {
      this.items.forEach((item) => {
        item.dataset.valid = 'false';
        this.clearStoredPricing(item);
        item.dataset.pricingState = 'unavailable';
        this.renderPriceValue(item, 'Unavailable', { unavailable: true });
        this.renderPriceMeta(item, message || '');
      });

      this.updateOrderTotal();
      this.updateSubmitState(true);
      this.showError(message || this.dataset.errorMessage || 'SparkLayer is unavailable right now.');
    }

    queuePriceRefresh(item, immediate) {
      const timeoutId = this.refreshTimers.get(item);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      const retryId = this.priceRetryTimers.get(item);
      if (retryId) {
        window.clearTimeout(retryId);
        this.priceRetryTimers.delete(item);
      }

      if (this.spark) {
        this.clearStoredPricing(item);
        item.dataset.pricingState = 'loading';
        this.updateOrderTotal();
      }

      if (this.isAccountContextRefreshPending) return;

      const delay = immediate ? 0 : 180;
      const nextTimeout = window.setTimeout(() => {
        this.refreshTimers.delete(item);

        if (!this.spark) return;
        this.refreshPrice(item);
      }, delay);

      this.refreshTimers.set(item, nextTimeout);
    }

    sanitizeQuantity(input) {
      const min = parseInt(input.min || '1', 10) || 1;
      let value = parseInt(input.value, 10);

      if (!Number.isFinite(value) || value < min) {
        value = min;
      }

      input.value = String(value);
      return value;
    }

    getItemQuantity(item) {
      const input = item.querySelector('.minimum-first-order__quantity-input');
      if (!input) return 1;
      return this.sanitizeQuantity(input);
    }

    // Keeps a row on "Loading..." and re-attempts the pricing lookup. SparkLayer often
    // returns empty pricing on the first calls after a cold load / account-context change,
    // so retrying lets the row recover without a manual page refresh.
    schedulePriceRetry(item, generation, attempt) {
      const existing = this.priceRetryTimers.get(item);
      if (existing) {
        window.clearTimeout(existing);
      }

      const delay = Math.min(500 * Math.pow(2, attempt - 1), 4000);
      const timeoutId = window.setTimeout(() => {
        this.priceRetryTimers.delete(item);

        if (!this.spark || generation !== this.pricingGeneration) return;
        this.refreshPrice(item, generation, attempt + 1);
      }, delay);

      this.priceRetryTimers.set(item, timeoutId);
    }

    // Safety net for the impersonation/generation race: if a row is still showing
    // "Loading..." after a price pass (because its in-flight fetch was discarded on a
    // generation change and nothing re-rendered it), re-kick it for the current generation.
    startPriceReconciliation() {
      this.stopPriceReconciliation();

      const startedAt = Date.now();
      const generation = this.pricingGeneration;

      const tick = () => {
        this.reconcileTimer = null;

        if (generation !== this.pricingGeneration) return;
        if (!this.spark || !this.isAuthenticated) return;

        this.items.forEach((item) => {
          if (
            item.dataset.pricingState === 'loading' &&
            !this.priceRetryTimers.has(item) &&
            !this.refreshTimers.has(item) &&
            !this.inFlightPrice.has(item)
          ) {
            this.refreshPrice(item, generation, 1);
          }
        });

        const unresolved = this.items.some((item) => item.dataset.pricingState === 'loading');
        if (unresolved && Date.now() - startedAt < 20000) {
          this.reconcileTimer = window.setTimeout(tick, 2000);
        }
      };

      this.reconcileTimer = window.setTimeout(tick, 2500);
    }

    stopPriceReconciliation() {
      if (this.reconcileTimer !== null) {
        window.clearTimeout(this.reconcileTimer);
        this.reconcileTimer = null;
      }
    }

    async refreshPrice(item, generation = this.pricingGeneration, attempt = 1) {
      if (!this.spark || generation !== this.pricingGeneration) return;

      const parentProductId = item.dataset.parentProductId;
      const variantSku = item.dataset.variantSku;
      const quantity = this.getItemQuantity(item);

      if (!parentProductId || !variantSku) {
        item.dataset.valid = 'false';
        this.clearStoredPricing(item);
        item.dataset.pricingState = 'unavailable';
        this.renderPriceValue(item, 'Unavailable', { unavailable: true });
        this.renderPriceMeta(item, 'Pricing unavailable for this SKU.');
        this.updateOrderTotal();
        this.updateSubmitState();
        return;
      }

      this.clearStoredPricing(item);
      item.dataset.pricingState = 'loading';
      this.renderPriceValue(item, 'Loading...', { loading: true });
      this.renderPriceMeta(item, '');
      this.updateOrderTotal();

      this.inFlightPrice.add(item);

      try {
        const pricing = await this.spark.calculatePricingForVariant(parentProductId, variantSku, quantity);

        if (generation !== this.pricingGeneration) return;

        const unitPrice = this.normalizeAmount(pricing && pricing.unitPrice);
        const lineTotal = this.normalizeAmount(pricing && pricing.totalPrice) ?? (unitPrice !== null ? unitPrice * quantity : null);

        if (!pricing || unitPrice === null || lineTotal === null) {
          // Cold SparkLayer context frequently returns empty pricing on the first calls.
          // Retry before giving up so the row recovers on its own instead of sticking.
          if (attempt < this.maxPriceAttempts) {
            this.schedulePriceRetry(item, generation, attempt);
            return;
          }
          throw new Error('Pricing unavailable for this variant.');
        }

        item.dataset.valid = 'true';
        item.dataset.pricingState = 'ready';
        item.dataset.lineTotal = String(lineTotal);
        item.dataset.currencyCode = pricing.currencyCode || '';
        this.renderPriceValue(
          item,
          this.formatMoney(lineTotal, pricing.currencyCode)
        );
        this.renderPriceMeta(
          item,
          `${this.formatMoney(unitPrice, pricing.currencyCode)} per unit`
        );
      } catch (error) {
        if (generation !== this.pricingGeneration) return;

        // Transient failures (SDK not warmed up, other apps interfering with fetch): retry.
        if (attempt < this.maxPriceAttempts) {
          console.warn('[MinimumFirstOrder] Pricing lookup retry', attempt, error);
          this.schedulePriceRetry(item, generation, attempt);
          return;
        }

        console.error('[MinimumFirstOrder] Pricing lookup failed', error);
        item.dataset.valid = 'false';
        this.clearStoredPricing(item);
        item.dataset.pricingState = 'unavailable';
        this.renderPriceValue(item, 'Unavailable', { unavailable: true });
        this.renderPriceMeta(item, 'Pricing unavailable for this SKU.');
      } finally {
        this.inFlightPrice.delete(item);

        if (generation === this.pricingGeneration) {
          this.updateOrderTotal();
          this.updateSubmitState();
        }
      }
    }

    normalizeAmount(amount) {
      if (amount === null || amount === undefined || amount === '') {
        return null;
      }

      const numericAmount = Number(amount);
      return Number.isFinite(numericAmount) ? numericAmount : null;
    }

    formatMoney(amount, currencyCode) {
      const normalizedCurrency = (currencyCode || 'USD').toUpperCase();

      try {
        return new Intl.NumberFormat(this.moneyLocale, {
          style: 'currency',
          currency: normalizedCurrency,
        }).format(amount);
      } catch (error) {
        return `${normalizedCurrency} ${amount}`;
      }
    }

    renderPriceValue(item, text, state = {}) {
      const priceValues = item.querySelectorAll('[data-price-value]');
      if (!priceValues.length) return;

      priceValues.forEach((priceValue) => {
        priceValue.textContent = text;
        priceValue.classList.toggle('is-loading', Boolean(state.loading));
        priceValue.classList.toggle('is-unavailable', Boolean(state.unavailable));
      });
    }

    renderPriceMeta(item, text) {
      const hasText = Boolean(text);
      const priceMetas = item.querySelectorAll('[data-price-meta]');
      if (!priceMetas.length) return;

      priceMetas.forEach((priceMeta) => {
        priceMeta.hidden = !hasText;
        priceMeta.textContent = text;
      });
    }

    clearStoredPricing(item) {
      delete item.dataset.lineTotal;
      delete item.dataset.currencyCode;
    }

    updateOrderTotal() {
      if (!this.submitButton || !this.totalElement) return;

      if (!this.items.length || !this.isAuthenticated || !this.spark) {
        this.renderOrderTotal();
        return;
      }

      const hasLoadingItems = this.items.some((item) => item.dataset.pricingState === 'loading');
      if (hasLoadingItems) {
        this.renderOrderTotal(this.totalLoadingMessage, { visible: true, loading: true });
        return;
      }

      const allItemsReady = this.items.every((item) => item.dataset.pricingState === 'ready');
      if (!allItemsReady) {
        this.renderOrderTotal();
        return;
      }

      const currencyCodes = Array.from(new Set(this.items.map((item) => item.dataset.currencyCode).filter(Boolean)));
      if (currencyCodes.length !== 1) {
        this.renderOrderTotal();
        return;
      }

      let total = 0;

      for (const item of this.items) {
        const lineTotal = this.normalizeAmount(item.dataset.lineTotal);
        if (lineTotal === null) {
          this.renderOrderTotal();
          return;
        }

        total += lineTotal;
      }

      this.renderOrderTotal(this.formatMoney(total, currencyCodes[0]), { visible: true });
    }

    renderOrderTotal(text = '', options = {}) {
      if (!this.submitButton || !this.totalElement) return;

      const { visible = false, loading = false } = options;

      this.totalElement.hidden = !visible;
      this.totalElement.textContent = visible ? text : '';
      this.totalElement.classList.toggle('is-loading', Boolean(loading));
      this.submitButton.classList.toggle('has-total', Boolean(visible));
    }

    getPayloadProducts() {
      return this.items
        .map((item) => {
          if (item.dataset.valid !== 'true') return null;

          const sku = item.dataset.variantSku;
          const quantity = this.getItemQuantity(item);

          if (!sku || quantity < 1) return null;

          return {
            sku,
            adjustQuantity: quantity,
          };
        })
        .filter(Boolean);
    }

    updateSubmitState(forceDisable) {
      if (!this.submitButton) return;

      const hasValidItems = this.getPayloadProducts().length > 0;
      const shouldDisable =
        Boolean(forceDisable) ||
        this.isSubmitting ||
        this.isAccountContextRefreshPending ||
        !this.isAuthenticated ||
        !this.spark ||
        !hasValidItems;

      this.submitButton.disabled = shouldDisable;
    }

    setSubmitting(isSubmitting) {
      if (!this.submitButton) return;

      this.isSubmitting = isSubmitting;
      this.submitButton.classList.toggle('is-loading', isSubmitting);
      this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
      this.updateSubmitState();
    }

    clearMessages() {
      if (this.statusElement) {
        this.statusElement.hidden = true;
        this.statusElement.textContent = '';
      }

      if (this.errorElement) {
        this.errorElement.hidden = true;
        this.errorElement.textContent = '';
      }
    }

    showStatus(message) {
      if (!this.statusElement) return;
      this.statusElement.hidden = false;
      this.statusElement.textContent = message;
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.hidden = false;
      this.errorElement.textContent = message;
    }

    async handleSubmit() {
      if (!this.spark || this.isSubmitting || this.isAccountContextRefreshPending) return;

      this.clearMessages();

      const products = this.getPayloadProducts();
      if (!products.length) {
        this.showError(this.dataset.emptyMessage || 'There are no valid recommendation lines to add.');
        this.updateSubmitState();
        return;
      }

      this.setSubmitting(true);

      try {
        const result = await this.spark.updateCart(
          {
            products,
            clearCart: false,
          },
          true,
          false
        );

        if (!result) {
          throw new Error(this.dataset.errorMessage || "We couldn't add the recommended order right now.");
        }

        this.showStatus(this.dataset.successMessage || 'Recommended order added to your B2B cart.');
        this.openSparkCart();
      } catch (error) {
        console.error('[MinimumFirstOrder] Cart update failed', error);
        this.showError(error.message || this.dataset.errorMessage || "We couldn't add the recommended order right now.");
      } finally {
        this.setSubmitting(false);
      }
    }

    openSparkCart() {
      if (this.spark && typeof this.spark.openDrawer === 'function') {
        this.spark.openDrawer('cart');
        return;
      }

      if (!document.querySelector('spark-drawer')) {
        const drawerElement = document.createElement('spark-drawer');
        drawerElement.setAttribute('initial-tab', 'cart');
        document.body.appendChild(drawerElement);
      }

      window.dispatchEvent(
        new CustomEvent('view-update', {
          bubbles: true,
          composed: true,
          detail: {
            view: 'cart',
          },
        })
      );
    }
  }

  customElements.define('minimum-first-order', MinimumFirstOrder);
}